import { authSubsGet, authSubsPost } from 'src/api/authSubs';
import {
  AskSalahBillingCycle,
  AskSalahPlanType,
  AskSalahSubscription,
  AskSalahTrialEligibility,
  SUBSCRIPTION_PRODUCT_ASK_SALAH,
} from 'src/types/askSalah';

export type CoreAskSalahPlan = {
  id: number;
  price: number;
  duration?: number;
  currency?: string;
  billingCycle?: AskSalahBillingCycle | null;
  subcriptionType?: string | null;
  subscriptionType?: string | null;
  features?: {
    planType?: AskSalahPlanType | string;
    sessionsLimit?: number;
    sessionsPerMonth?: number;
  };
};

type CoreSubscriptionRow = {
  id: number;
  startDate: string;
  endDate: string;
  status: string;
  type?: string;
  product?: string;
  metadata?: Record<string, unknown>;
  order?: {
    quotation?: CoreAskSalahPlan;
  };
};

const inferPlanType = (plan?: CoreAskSalahPlan): AskSalahPlanType | string => {
  if (plan?.features?.planType) {
    return String(plan.features.planType).toLowerCase();
  }
  const subType = plan?.subcriptionType || plan?.subscriptionType;
  if (subType === 'free_trial') return 'trial';
  return 'basic';
};

export const mapCorePlanToAskSalahQuotation = (
  plan: CoreAskSalahPlan,
) => {
  const planType = inferPlanType(plan) as AskSalahPlanType;
  return {
    id: String(plan.id),
    quotationId: plan.id,
    planType,
    billingCycle: plan.billingCycle ?? undefined,
    price: Number(plan.price),
    sessionsPerMonth:
      plan.features?.sessionsLimit ??
      plan.features?.sessionsPerMonth ??
      (planType === 'trial' ? 3 : 10),
  };
};

export const mapCoreSubscriptionToAskSalah = (
  sub: CoreSubscriptionRow | null | undefined,
): AskSalahSubscription | null => {
  if (!sub) return null;

  const metadata = sub.metadata || {};
  const quotation = sub.order?.quotation;
  const planType =
    (metadata.planType as string) ||
    inferPlanType(quotation);

  return {
    id: sub.id,
    planType,
    billingCycle:
      (metadata.billingCycle as AskSalahBillingCycle | null | undefined) ??
      quotation?.billingCycle ??
      undefined,
    startDate: sub.startDate,
    endDate: sub.endDate,
    status: sub.status,
    sessionsUsedThisMonth: Number(metadata.sessionsUsedThisMonth ?? 0),
    sessionsLimit: Number(
      metadata.sessionsLimit ??
        quotation?.features?.sessionsLimit ??
        quotation?.features?.sessionsPerMonth ??
        10,
    ),
    lastSessionResetDate: metadata.lastSessionResetDate as string | undefined,
  };
};

export const fetchAskSalahQuotations = (
  locale?: string,
): Promise<CoreAskSalahPlan[]> =>
  authSubsGet<CoreAskSalahPlan[]>(
    `/subscription/quotation?product=${SUBSCRIPTION_PRODUCT_ASK_SALAH}`,
    locale,
  );

export const fetchAskSalahSubscriptions = (
  locale?: string,
): Promise<CoreSubscriptionRow[]> =>
  authSubsGet<CoreSubscriptionRow[]>(
    `/subscription/my?product=${SUBSCRIPTION_PRODUCT_ASK_SALAH}`,
    locale,
  );

export const fetchActiveAskSalahSubscription = async (
  locale?: string,
): Promise<AskSalahSubscription | null> => {
  const rows = await fetchAskSalahSubscriptions(locale);
  if (!Array.isArray(rows) || !rows.length) return null;

  const now = new Date();
  const active = rows.find(
    (row) =>
      row.status === 'active' &&
      row.endDate &&
      new Date(row.endDate) > now,
  );
  return active ? mapCoreSubscriptionToAskSalah(active) : null;
};

export const checkAskSalahTrialEligibility = async (
  locale?: string,
): Promise<AskSalahTrialEligibility> => {
  const rows = await fetchAskSalahSubscriptions(locale);
  if (!Array.isArray(rows)) {
    return { eligible: false, reason: 'already_used' };
  }

  const now = new Date();
  const hasActive = rows.some(
    (row) =>
      row.status === 'active' &&
      row.endDate &&
      new Date(row.endDate) > now,
  );
  if (hasActive) {
    return { eligible: false, reason: 'has_paid_subscription' };
  }

  if (rows.length > 0) {
    return { eligible: false, reason: 'already_used' };
  }

  return { eligible: true };
};

export const startAskSalahInstantTrial = (
  locale?: string,
): Promise<{ success: boolean; message: string }> =>
  authSubsPost('/subscription/instant-trial', {
    product: SUBSCRIPTION_PRODUCT_ASK_SALAH,
  }, locale);

export const createAskSalahCoreOrder = (
  data: {
    quotationId: number;
    paymentMethod: string;
    proveUrl?: string;
    gateway?: 'tap' | 'myfatoorah';
  },
  locale?: string,
): Promise<{ id: number; url?: string }> =>
  authSubsPost('/subscription/order', {
    ...data,
    product: SUBSCRIPTION_PRODUCT_ASK_SALAH,
  }, locale);

export const repurchaseAskSalahOrder = (
  data?: { gateway?: 'tap' | 'myfatoorah' },
  locale?: string,
): Promise<{ id: number; url?: string }> =>
  authSubsPost(
    `/subscription/order/repurchase?product=${SUBSCRIPTION_PRODUCT_ASK_SALAH}`,
    data ?? {},
    locale,
  );
